import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Terrain as SiteIcon,
  GraphicEq as AudioIcon,
  Sensors as SensorIcon,
  AccessTime as TimeIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { MinimalBirdIcon as SpeciesIcon } from './MinimalBirdIcon';
import { BioSite, BioDeployment } from '../bioacousticMockData';
import SpeciesActivityTrendChart from './SpeciesActivityTrendChart';
import AcousticIndicesChart from './AcousticIndicesChart';
import SpeciesDistributionDonut from './SpeciesDistributionDonut';

interface SiteLevelSectionProps {
  sites: BioSite[];
  activeSite: BioSite;
  onSelectSite: (site: BioSite) => void;
  activeDeploymentId: string;
  onSelectDeployment: (deployment: BioDeployment) => void;
}

const SiteLevelSection: React.FC<SiteLevelSectionProps> = ({
  sites,
  activeSite,
  onSelectSite,
  activeDeploymentId,
  onSelectDeployment,
}) => {
  return (
    <Box
      id="site-level-section"
      sx={{
        mb: 6,
        pt: 3,
        borderTop: '1px dashed #2A2A2A',
      }}
    >
      {/* Level Header Banner */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                bgcolor: 'rgba(0, 212, 255, 0.15)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '4px',
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <SiteIcon sx={{ fontSize: 13, color: '#00D4FF' }} />
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#00D4FF', letterSpacing: '1px' }}>
                LEVEL 2 : SITE LEVEL
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 800,
              color: '#FFFFFF',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            {activeSite.site_name}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#666666', mt: 0.25 }}>
            Site-level diurnal activity cycles, soundscape indices, and species threat distribution
          </Typography>
        </Box>

        {/* Site Switcher Dropdown */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '11px', color: '#666666', fontWeight: 700, textTransform: 'uppercase' }}>
            Switch Site:
          </Typography>
          <Select
            size="small"
            value={activeSite.site_id}
            onChange={(e) => {
              const target = sites.find((s) => s.site_id === e.target.value);
              if (target) onSelectSite(target);
            }}
            sx={{
              bgcolor: '#111111',
              color: '#00D4FF',
              fontWeight: 700,
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #2A2A2A',
              '& .MuiSelect-select': { py: 0.75, px: 1.5 },
              '& .MuiSvgIcon-root': { color: '#00D4FF' },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#111',
                  border: '1px solid #222',
                  color: '#fff',
                  '& .MuiMenuItem-root': {
                    fontSize: '12px',
                    '&.Mui-selected': { bgcolor: 'rgba(0,212,255,0.15)', color: '#00D4FF' },
                  },
                },
              },
            }}
          >
            {sites.map((s) => (
              <MenuItem key={s.site_id} value={s.site_id}>
                {s.site_name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* KPI Cards Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        {[
          {
            label: 'Species Identified',
            value: activeSite.totalSpecies,
            sub: 'At this specific site',
            icon: SpeciesIcon,
            color: '#10B981',
          },
          {
            label: 'Site Audio Recordings',
            value: activeSite.totalAudioFiles.toLocaleString(),
            sub: 'Acoustic recordings logged',
            icon: AudioIcon,
            color: '#00D4FF',
          },
          {
            label: 'Active Deployments',
            value: activeSite.totalDeployments,
            sub: 'Sampling nodes active',
            icon: SensorIcon,
            color: '#F59E0B',
          },
          {
            label: 'Peak Vocal Period',
            value: '06:00 - 08:30',
            sub: 'Dawn chorus biophony peak',
            icon: TimeIcon,
            color: '#8B5CF6',
          },
        ].map((kpi) => {
          const IconComp = kpi.icon;
          return (
            <Box
              key={kpi.label}
              sx={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #1E1E1E',
                borderRadius: '10px',
                p: '14px 16px',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: `${kpi.color}55`,
                  background: 'rgba(255, 255, 255, 0.04)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography sx={{ fontSize: '10px', color: '#777777', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                  {kpi.label}
                </Typography>
                <IconComp sx={{ fontSize: 18, color: kpi.color, opacity: 0.8 }} />
              </Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.1 }}>
                {kpi.value}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: '#555555', mt: 0.75 }}>
                {kpi.sub}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Grid: Acoustic Indices Analysis & Species Distribution */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 2, mb: 3 }}>
        <AcousticIndicesChart
          deploymentName={activeSite.deployments[0]?.deployment_name || 'Site Baseline'}
          height={340}
        />
        <SpeciesDistributionDonut
          data={activeSite.threatDistribution}
          title="Site Species Distribution"
          height={340}
        />
      </Box>

      {/* Species Activity Trend Full Width */}
      <Box sx={{ mb: 3 }}>
        <SpeciesActivityTrendChart
          title="Species Activity Trend"
          height={360}
        />
      </Box>

      {/* List of Deployments Directory */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#00D4FF', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Deployments Directory
            </Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#FFFFFF' }}>
              Deployments at {activeSite.site_name} ({activeSite.deployments.length})
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#555555' }}>
            Click on any deployment to view deployment-level analysis & species catalogue
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {activeSite.deployments.map((dep) => {
            const isSelected = dep.deployment_id === activeDeploymentId;
            return (
              <Box
                key={dep.deployment_id}
                onClick={() => onSelectDeployment(dep)}
                sx={{
                  background: isSelected ? 'rgba(0, 212, 255, 0.08)' : 'rgba(10, 10, 10, 0.85)',
                  border: `1px solid ${isSelected ? '#00D4FF' : '#1E1E1E'}`,
                  borderRadius: '10px',
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#00D4FF',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isSelected ? '#00D4FF' : '#FFFFFF' }}>
                      {dep.deployment_name}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: '#10B981', mt: 0.25, fontWeight: 700 }}>
                      {dep.device_type} // {dep.device_id}
                    </Typography>
                  </Box>
                  <SensorIcon sx={{ fontSize: 18, color: isSelected ? '#00D4FF' : '#555555' }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, my: 1.5, pt: 1, borderTop: '1px solid #1A1A1A' }}>
                  <Box>
                    <Typography sx={{ fontSize: '9px', color: '#555555', textTransform: 'uppercase' }}>Species</Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#10B981' }}>{dep.totalSpecies}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '9px', color: '#555555', textTransform: 'uppercase' }}>Audios</Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#00D4FF' }}>{dep.totalAudioFiles}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '9px', color: '#555555', textTransform: 'uppercase' }}>Avg Conf.</Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#F59E0B' }}>{dep.avgConfidence}%</Typography>
                  </Box>
                </Box>

                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    width: '100%',
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    color: isSelected ? '#00D4FF' : '#888888',
                    bgcolor: isSelected ? 'rgba(0, 212, 255, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                    border: `1px solid ${isSelected ? 'rgba(0, 212, 255, 0.3)' : '#222222'}`,
                    borderRadius: '6px',
                    py: 0.5,
                    '&:hover': {
                      bgcolor: '#00D4FF',
                      color: '#000000',
                    },
                  }}
                >
                  {isSelected ? 'Currently Selected' : 'View Deployment Analysis'}
                </Button>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default SiteLevelSection;
